class AllBlogsList extends HTMLElement {
  connectedCallback() {
    this.postsPerPage = parseInt(this.dataset.postsPerPage) || 9;
    this.store = this.querySelector('[data-all-blogs-store]');
    this.grid = this.querySelector('[data-all-blogs-grid]');
    this.paginationEl = this.querySelector('[data-all-blogs-pagination]');
    this.tabs = [...this.querySelectorAll('[data-blog-tab]')];
    this.sortInputs = [...this.querySelectorAll('[data-blog-sort-input]')];
    this.sortLabel = this.querySelector('[data-blog-sort-label]');
    this.sortDetails = this.querySelector('[data-blog-sort-input]')?.closest('details');
    this.allItems = [...this.store.querySelectorAll('[data-blog-item]')];

    this.readUrl();
    this.render();

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', (e) => {
        e.preventDefault();
        this.navigate(tab.dataset.blogHandle, this.sortBy, 1);
      });
    });

    // Radio sort inputs
    this.sortInputs.forEach((input) => {
      input.addEventListener('change', () => {
        if (input.checked) {
          this.navigate(this.blog, input.value, 1);
          // Close the details panel
          if (this.sortDetails) this.sortDetails.removeAttribute('open');
        }
      });
    });

    window.addEventListener('popstate', () => {
      this.readUrl();
      this.render();
    });
  }

  readUrl() {
    const p = new URLSearchParams(window.location.search);
    this.blog = p.get('blog') || 'all';
    this.sortBy = p.get('sort_by') || 'published-descending';
    this.page = parseInt(p.get('page') || '1');
  }

  navigate(blog, sortBy, page) {
    const p = new URLSearchParams();
    if (blog && blog !== 'all') p.set('blog', blog);
    if (sortBy) p.set('sort_by', sortBy);
    if (page > 1) p.set('page', String(page));
    const qs = p.toString();
    history.pushState({}, '', window.location.pathname + (qs ? '?' + qs : ''));
    this.blog = blog;
    this.sortBy = sortBy;
    this.page = page;
    this.render();
  }

  render() {
    // Active tab
    this.tabs.forEach((tab) => {
      const active = tab.dataset.blogHandle === this.blog;
      tab.classList.toggle('all-blogs__tab--active', active);
      tab.setAttribute('aria-selected', String(active));
    });

    // Sync sort UI
    const labels = { 'published-descending': 'Most recent', 'published-ascending': 'Oldest first' };
    if (this.sortLabel) this.sortLabel.textContent = labels[this.sortBy] || 'Most recent';
    this.sortInputs.forEach((input) => {
      const isActive = input.value === this.sortBy;
      input.checked = isActive;
      const optionDiv = input.closest('[role="option"]');
      if (optionDiv) {
        optionDiv.setAttribute('aria-selected', String(isActive));
        optionDiv.setAttribute('tabindex', isActive ? '0' : '-1');
      }
    });

    // Filter
    let filtered = this.allItems.filter(
      (item) => this.blog === 'all' || item.dataset.blog === this.blog
    );

    // Sort
    filtered.sort((a, b) => {
      const diff = parseInt(a.dataset.published) - parseInt(b.dataset.published);
      return this.sortBy === 'published-ascending' ? diff : -diff;
    });

    // Paginate
    const totalPages = Math.max(1, Math.ceil(filtered.length / this.postsPerPage));
    if (this.page > totalPages) this.page = 1;
    const start = (this.page - 1) * this.postsPerPage;
    const pageItems = filtered.slice(start, start + this.postsPerPage);

    // Render grid
    this.grid.innerHTML = '';
    if (pageItems.length === 0) {
      this.grid.innerHTML = '<p class="all-blogs__empty">No posts found.</p>';
    } else {
      pageItems.forEach((item) => this.grid.appendChild(item.cloneNode(true)));
    }

    // Render pagination
    this.renderPagination(this.page, totalPages);
  }

  renderPagination(current, total) {
    if (!this.paginationEl) return;
    if (total <= 1) { this.paginationEl.innerHTML = ''; return; }

    const chevL = `<svg width="6" height="10" viewBox="0 0 6 10" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M5 1L1 5L5 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
    const chevR = `<svg width="6" height="10" viewBox="0 0 6 10" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M1 1L5 5L1 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

    const prevContent = `${chevL} <span>Previous</span>`;
    const nextContent = `<span>Next</span> ${chevR}`;

    // Build visible page set
    const visible = new Set([1, total]);
    for (let i = Math.max(1, current - 1); i <= Math.min(total, current + 1); i++) visible.add(i);
    const pages = [...visible].sort((a, b) => a - b);

    // Insert ellipsis markers
    const items = [];
    let prev = 0;
    for (const p of pages) {
      if (prev && p - prev > 1) items.push(null);
      items.push(p);
      prev = p;
    }

    const btn = (p, label) =>
      `<li class="all-blogs-pagination__item"><button type="button" class="all-blogs-pagination__link" data-page="${p}" aria-label="${label}">${p}</button></li>`;

    let html = `<nav class="all-blogs-pagination">`;

    // Prev
    html += `<div class="all-blogs-pagination__nav all-blogs-pagination__nav--prev">${current > 1
      ? `<button type="button" class="all-blogs-pagination__link-arrow" data-page="${current - 1}" aria-label="Previous">${prevContent}</button>`
      : `<span class="all-blogs-pagination__link-arrow all-blogs-pagination__link--disabled" aria-disabled="true">${prevContent}</span>`
      }</div>`;

    html += `<ul class="all-blogs-pagination__list" role="list">`;

    // Pages
    for (const p of items) {
      if (p === null) {
        html += `<li class="all-blogs-pagination__item"><span class="all-blogs-pagination__link all-blogs-pagination__link--gap" aria-hidden="true">&hellip;</span></li>`;
      } else if (p === current) {
        html += `<li class="all-blogs-pagination__item"><span class="all-blogs-pagination__link all-blogs-pagination__link--current" aria-current="page">${p}</span></li>`;
      } else {
        html += btn(p, `Page ${p}`);
      }
    }

    html += `</ul>`;

    // Next
    html += `<div class="all-blogs-pagination__nav all-blogs-pagination__nav--next">${current < total
      ? `<button type="button" class="all-blogs-pagination__link-arrow" data-page="${current + 1}" aria-label="Next">${nextContent}</button>`
      : `<span class="all-blogs-pagination__link-arrow all-blogs-pagination__link--disabled" aria-disabled="true">${nextContent}</span>`
      }</div>`;

    html += `</nav>`;
    this.paginationEl.innerHTML = html;

    this.paginationEl.querySelectorAll('[data-page]').forEach((b) => {
      b.addEventListener('click', () => this.navigate(this.blog, this.sortBy, parseInt(b.dataset.page)));
    });
  }
}

if (!customElements.get('all-blogs-list')) {
  customElements.define('all-blogs-list', AllBlogsList);
}
